import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

const HeroSection = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: #030508;

  /* Gold separator line at the bottom */
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(201,168,76,0.3) 15%,
      rgba(201,168,76,0.85) 35%,
      #C9A84C 50%,
      rgba(201,168,76,0.85) 65%,
      rgba(201,168,76,0.3) 85%,
      transparent 100%
    );
    z-index: 10;
  }

  @keyframes heroGlow {
    from { transform: scale(1) translateY(0px); opacity: 0.7; }
    to   { transform: scale(1.1) translateY(18px); opacity: 1; }
  }

  @keyframes rotateOrb {
    from { transform: rotate(0deg) translateX(180px) rotate(0deg); }
    to   { transform: rotate(360deg) translateX(180px) rotate(-360deg); }
  }

  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(36px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes shimmerText {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }

  @keyframes pulseGold {
    0%, 100% { box-shadow: 0 0 0 0 rgba(201,168,76,0); }
    50%       { box-shadow: 0 0 0 8px rgba(201,168,76,0.12); }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    33%       { transform: translateY(-14px) rotate(2deg); }
    66%       { transform: translateY(-6px) rotate(-1deg); }
  }

  @keyframes scanLine {
    0%   { transform: translateY(-100%); opacity: 0; }
    10%  { opacity: 1; }
    90%  { opacity: 1; }
    100% { transform: translateY(100vh); opacity: 0; }
  }

  /* ===== RESPONSIVE ===== */
  @media (min-width: 1536px) { min-height: 110vh; }
  @media (max-width: 1024px) { min-height: auto; padding: 7rem 0 5rem; }
  @media (max-width: 768px)  { padding: 6rem 0 4rem; }
  @media (max-width: 640px)  { padding: 5rem 0 3.5rem; }
  @media (max-width: 480px)  { padding: 4.5rem 0 3rem; }
  @media (max-width: 320px)  { padding: 4rem 0 2.5rem; }
`;

/* Canvas for animated background scene */
const HeroCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  opacity: 1;
`;

/* Deep ambient glow orbs */
const HeroOrbs = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;

  /* Primary gold orb - top right */
  &::before {
    content: "";
    position: absolute;
    width: 800px; height: 800px;
    top: -300px; right: -220px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(201,168,76,0.13) 0%, transparent 65%);
    filter: blur(40px);
    animation: heroGlow 9s ease-in-out infinite alternate;
  }

  /* Blue-navy orb - bottom left */
  &::after {
    content: "";
    position: absolute;
    width: 700px; height: 700px;
    bottom: -250px; left: -180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(30,80,160,0.18) 0%, transparent 65%);
    filter: blur(50px);
    animation: heroGlow 12s ease-in-out infinite alternate-reverse;
  }
`;

/* Orbiting gold accent ring */
const HeroRing = styled.div`
  position: absolute;
  width: 520px; height: 520px;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: none;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid rgba(201,168,76,0.08);
  }

  &::after {
    content: "";
    position: absolute;
    width: 6px; height: 6px;
    top: 0; left: 50%;
    margin-left: -3px;
    background: #C9A84C;
    border-radius: 50%;
    box-shadow: 0 0 12px 4px rgba(201,168,76,0.5);
    animation: rotateOrb 18s linear infinite;
    transform-origin: 3px 260px;
  }

  @media (max-width: 768px) {
    width: 320px; height: 320px;
    &::after { transform-origin: 3px 160px; }
  }
`;

/* Horizontal scan line – gives a futuristic data feel */
const ScanLine = styled.div`
  position: absolute;
  left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent 0%, rgba(201,168,76,0.25) 40%, rgba(201,168,76,0.25) 60%, transparent 100%);
  z-index: 2;
  animation: scanLine 8s ease-in-out infinite;
  pointer-events: none;
`;

/* Dark radial overlay — keeps text legible without killing the animation */
const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
    radial-gradient(ellipse at 50% 40%, rgba(3,5,8,0.15) 0%, rgba(3,5,8,0.52) 100%);
  pointer-events: none;
`;

const HeroBgMedia = styled.div`
  position: absolute;
  top: 0; left: 0;
  width: 100vw; height: 100vh;
  z-index: 0;
  overflow: hidden;

  video {
    width: 100vw; height: 100vh;
    object-fit: cover;
    min-width: 100%; min-height: 100%;
    max-width: 100vw; max-height: 100vh;
    filter: brightness(0.22) saturate(0.6);
    @media (max-width: 768px) { object-position: center; }
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(5,8,15,0.82) 0%, rgba(5,8,15,0.65) 100%);
  }
`;

const HeroFloatingIcons = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  overflow: hidden;

  i {
    position: absolute;
    color: rgba(201,168,76,0.12);
    animation: float 7s ease-in-out infinite;
    filter: drop-shadow(0 0 8px rgba(201,168,76,0.2));

    &:nth-child(1) { top: 14%; left: 6%;  font-size: 2.8rem; animation-duration: 6s; }
    &:nth-child(2) { top: 62%; left: 88%; font-size: 3.4rem; animation-duration: 8s;  animation-delay: 1.2s; color: rgba(201,168,76,0.09); }
    &:nth-child(3) { top: 25%; left: 80%; font-size: 2.5rem; animation-duration: 5.5s; animation-delay: 0.6s; color: rgba(201,168,76,0.14); }
    &:nth-child(4) { top: 78%; left: 12%; font-size: 3rem;   animation-duration: 7s;  animation-delay: 2s; }
    &:nth-child(5) { top: 48%; left: 5%;  font-size: 2.8rem; animation-duration: 6.5s; animation-delay: 1.8s; color: rgba(201,168,76,0.10); }

    @media (max-width: 768px) {
      font-size: 1.8rem !important;
      opacity: 0.5;
    }
  }
`;

const HeroMain = styled.div`
  position: relative;
  z-index: 5;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2.2rem;
  padding: 4rem 2rem;
  animation: fadeInUp 1s ease-out both;

  @media (min-width: 1536px) { max-width: 1350px; gap: 3rem; padding: 5rem 2rem; }
  @media (max-width: 1280px) { max-width: 1000px; }
  @media (max-width: 1024px) { max-width: 900px; padding: 3rem 1.8rem; }
  @media (max-width: 768px)  { gap: 2rem; padding: 2.5rem 1.3rem; }
  @media (max-width: 640px)  { gap: 1.8rem; padding: 2rem 1rem; }
  @media (max-width: 480px)  { gap: 1.5rem; padding: 1.5rem 0.9rem; }
  @media (max-width: 320px)  { gap: 1.2rem; padding: 1.2rem 0.7rem; }
`;

/* Eyebrow label above heading */
const EyebrowTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-family: 'Outfit', sans-serif;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #C9A84C;
  border: 1px solid rgba(201,168,76,0.3);
  background: rgba(201,168,76,0.08);
  padding: 0.55rem 1.2rem;
  border-radius: 2px;
  backdrop-filter: blur(10px);
  animation: fadeInDown 0.9s ease-out 0.2s both;

  &::before, &::after {
    content: "";
    width: 20px; height: 1px;
    background: linear-gradient(90deg, transparent, #C9A84C);
    display: inline-block;
  }
  &::after {
    background: linear-gradient(90deg, #C9A84C, transparent);
  }

  @media (max-width: 480px) { font-size: 0.65rem; padding: 0.45rem 0.9rem; }
`;

const HeroTitle = styled.h1`
  color: #F8FAFC;
  line-height: 1.04;
  margin: 0;

  .main-title {
    display: block;
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: clamp(3.4rem, 7vw, 6.2rem);
    font-weight: 600;
    letter-spacing: -1.5px;
    color: #FFFFFF;
    /* Layered text shadows for crystal-clear legibility over any background */
    text-shadow:
      0 2px 4px rgba(0,0,0,0.9),
      0 8px 32px rgba(0,0,0,0.8),
      0 0 80px rgba(0,0,0,0.5);
    margin-bottom: 0.4rem;
    animation: fadeInUp 1s ease-out 0.3s both;
  }

  .sub-title {
    display: block;
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: clamp(1.9rem, 4.5vw, 3.8rem);
    font-weight: 300;
    font-style: italic;
    letter-spacing: 0.5px;
    animation: fadeInUp 1s ease-out 0.5s both;

    /* Shimmer gold gradient on the subtitle */
    background: linear-gradient(
      100deg,
      #E2C47A 0%,
      #F5E8C0 30%,
      #C9A84C 50%,
      #F5E8C0 70%,
      #E2C47A 100%
    );
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: fadeInUp 1s ease-out 0.5s both, shimmerText 4s linear 1.5s infinite;

    span {
      display: inline;
      margin-right: 0.3em;
    }
  }

  @media (min-width: 1536px) {
    .main-title { font-size: 7rem; }
    .sub-title  { font-size: 4.5rem; }
  }
  @media (max-width: 1024px) {
    .main-title { letter-spacing: -1px; }
  }
  @media (max-width: 768px) {
    .main-title { font-size: clamp(2.8rem, 8vw, 4rem); }
    .sub-title  { font-size: clamp(1.6rem, 5vw, 2.5rem); }
  }
  @media (max-width: 480px) {
    .main-title { font-size: 2.4rem; line-height: 1.1; }
    .sub-title  { font-size: 1.4rem; line-height: 1.3; }
  }
  @media (max-width: 320px) {
    .main-title { font-size: 2rem; }
    .sub-title  { font-size: 1.2rem; }
  }
`;

const HeroSubtitle = styled.div`
  font-size: clamp(1.05rem, 3vw, 1.3rem);
  color: rgba(240,233,214,0.88);
  font-weight: 300;
  max-width: 680px;
  line-height: 1.75;
  font-family: 'Outfit', sans-serif;
  letter-spacing: 0.02em;
  padding: 0 1rem;
  animation: fadeInUp 1s ease-out 0.7s both;
  /* Strong text shadow for legibility */
  text-shadow: 0 2px 12px rgba(0,0,0,0.85), 0 0 40px rgba(0,0,0,0.6);

  .highlight {
    color: #E2C47A;
    font-weight: 500;
  }

  @media (max-width: 768px) { font-size: clamp(0.95rem, 3.5vw, 1.1rem); padding: 0 0.5rem; }
  @media (max-width: 480px) { font-size: 0.95rem; line-height: 1.65; }
  @media (max-width: 360px) { font-size: 0.9rem; }
`;

const HeroSearchContainer = styled.div`
  width: 100%;
  max-width: 700px;
  margin: 0.5rem auto 0;
  position: relative;
  padding: 0 1rem;
  animation: fadeInUp 1s ease-out 0.9s both;
  z-index: 100;

  @media (max-width: 768px) { max-width: 100%; padding: 0 0.5rem; }
`;

const HeroSearchWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  background: rgba(5,8,15,0.55);
  padding: 0.4rem;
  border-radius: 3px;
  backdrop-filter: blur(24px);
  border: 1px solid rgba(201,168,76,0.35);
  box-shadow:
    0 0 0 1px rgba(201,168,76,0.08),
    0 16px 48px rgba(0,0,0,0.5),
    0 0 40px rgba(201,168,76,0.12);
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:focus-within {
    border-color: #C9A84C;
    box-shadow:
      0 0 0 3px rgba(201,168,76,0.12),
      0 16px 48px rgba(0,0,0,0.5);
  }

  @media (max-width: 768px) { padding: 0.3rem; }

  .search-icon {
    position: absolute;
    left: 1.4rem;
    top: 50%;
    transform: translateY(-50%);
    color: #C9A84C;
    font-size: 1.2rem;
    pointer-events: none;
    z-index: 5;
    filter: drop-shadow(0 0 6px rgba(201,168,76,0.5));

    @media (max-width: 768px) { left: 1rem; font-size: 1rem; }
  }

  input {
    width: 100%;
    padding: 1.1rem 1.5rem 1.1rem 3.6rem;
    border-radius: 2px;
    border: none;
    font-size: 1rem;
    background: rgba(240,233,214,0.06);
    color: #F0E9D6;
    outline: none;
    transition: background 0.3s ease;
    font-weight: 400;
    font-family: 'Outfit', sans-serif;
    letter-spacing: 0.02em;

    &:focus { background: rgba(240,233,214,0.1); }

    &::placeholder { color: rgba(168,180,200,0.6); }

    @media (max-width: 768px) { padding: 0.95rem 1rem 0.95rem 3rem; font-size: 0.95rem; }
    @media (max-width: 480px)  { padding: 0.85rem 0.9rem 0.85rem 2.8rem; font-size: 0.9rem; }
    @media (hover: none) and (pointer: coarse) { font-size: 16px; }
  }
`;

const SearchResults = styled.ul`
  list-style: none;
  padding: 0.5rem 0;
  margin: 0.5rem 0 0;
  width: 100%;
  background: rgba(10,15,28,0.98);
  border-radius: 3px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.6);
  display: none;
  max-height: 320px;
  overflow-y: auto;
  position: absolute;
  z-index: 9999;
  top: 100%;
  left: 0;
  right: 0;
  backdrop-filter: blur(24px);
  border: 1px solid rgba(201,168,76,0.25);

  &.show { display: block; }

  li {
    padding: 0.85rem 1.4rem;
    cursor: pointer;
    border-bottom: 1px solid rgba(201,168,76,0.08);
    color: #A8B4C8;
    font-size: 0.9rem;
    font-family: 'Outfit', sans-serif;
    font-weight: 400;
    transition: background 0.2s, color 0.2s, padding-left 0.2s;
    display: flex;
    align-items: center;
    gap: 0.6rem;

    &::before {
      content: "→";
      color: #C9A84C;
      font-size: 0.8rem;
      opacity: 0;
      transition: opacity 0.2s;
      flex-shrink: 0;
    }

    &:last-child { border-bottom: none; }

    &:hover {
      background: rgba(201,168,76,0.08);
      color: #E2C47A;
      padding-left: 1.8rem;
      &::before { opacity: 1; }
    }
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 0.8rem;
  max-width: 100%;
  padding: 0 1rem;
  animation: fadeInUp 1s ease-out 1.1s both;

  @media (max-width: 768px) { gap: 0.6rem; padding: 0 0.5rem; flex-wrap: wrap; }
  @media (max-width: 480px) { flex-direction: column; align-items: stretch; }
`;

const HeroButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: 'Outfit', sans-serif;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  text-decoration: none;
  padding: 0.8rem 1.4rem;
  border-radius: 2px;
  border: 1px solid rgba(201,168,76,0.3);
  background: rgba(201,168,76,0.08);
  color: #E2C47A;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  backdrop-filter: blur(12px);
  flex: 0 0 auto;
  animation: pulseGold 3s ease-in-out infinite;

  i { font-size: 0.75rem; }

  &:hover {
    background: #C9A84C;
    color: #05080F;
    border-color: #C9A84C;
    transform: translateY(-2px);
    box-shadow: 0 6px 24px rgba(201,168,76,0.35);
    text-decoration: none;
    animation: none;
  }

  /* First button (primary) gets a solid treatment */
  &:first-child {
    background: #C9A84C;
    color: #05080F;
    border-color: #C9A84C;
    animation: pulseGold 3s ease-in-out infinite;

    &:hover {
      background: #E2C47A;
      border-color: #E2C47A;
    }
  }

  @media (max-width: 768px) {
    padding: 0.85rem 1.2rem;
    font-size: 0.78rem;
    flex: 1 1 calc(50% - 0.3rem);
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex: 1 1 100%;
    padding: 0.85rem 1rem;
  }

  @media (hover: none) and (pointer: coarse) {
    -webkit-tap-highlight-color: rgba(201,168,76,0.3);
    touch-action: manipulation;
  }
`;

const TrustBadge = styled.div`
  margin-bottom: 0;
  animation: fadeInDown 1s ease-out 0.1s both;
  display: flex;
  justify-content: center;

  .trust-content {
    display: inline-flex;
    align-items: center;
    background: rgba(5,8,15,0.6);
    color: #F0E9D6;
    font-family: 'Outfit', sans-serif;
    font-weight: 500;
    font-size: 0.9rem;
    padding: 0.6rem 1.4rem;
    border-radius: 2px;
    letter-spacing: 0.04em;
    box-shadow: 0 8px 32px rgba(0,0,0,0.4);
    backdrop-filter: blur(16px);
    border: 1px solid rgba(201,168,76,0.25);
    gap: 0.6rem;
    flex-wrap: wrap;
    justify-content: center;

    .google-icon {
      height: 26px;
      margin-right: 6px;
      vertical-align: middle;
      background: #fff;
      border-radius: 50%;
      padding: 3px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    }

    .client-avatars {
      display: flex;
      margin-right: 8px;

      img {
        height: 26px;
        width: 26px;
        border-radius: 50%;
        margin-right: -8px;
        border: 2px solid rgba(201,168,76,0.5);
        vertical-align: middle;
      }

      img:last-child { margin-right: 8px; }
    }

    .stars {
      font-size: 1.1rem;
      color: #C9A84C;
      vertical-align: middle;
      filter: drop-shadow(0 0 6px rgba(201,168,76,0.6));
      margin-right: 6px;
    }

    .rating-text {
      font-size: 1rem;
      color: #E2C47A;
      font-weight: 700;
      margin-left: 4px;
      vertical-align: middle;
    }

    @media (max-width: 768px) {
      font-size: 0.82rem;
      padding: 0.45rem 0.9rem;
      .google-icon { height: 22px; }
      .client-avatars img { height: 22px; width: 22px; }
      .stars { font-size: 0.95rem; }
      .rating-text { font-size: 0.88rem; }
    }

    @media (max-width: 480px) {
      padding: 0.4rem 0.7rem;
      font-size: 0.78rem;
      .google-icon { height: 20px; }
      .client-avatars img { height: 20px; width: 20px; }
    }
  }
`;

const Hero = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const canvasRef = useRef(null);

  // ── Rich multi-layer canvas animation ──────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    let t = 0; // global time ticker
    let animId;

    // ── 1. Stars / deep field ──────────────────────────────────────
    const STAR_COUNT = 180;
    const stars = Array.from({ length: STAR_COUNT }, () => ({
      x:  Math.random(),
      y:  Math.random(),
      r:  Math.random() * 1.1 + 0.2,
      a:  Math.random() * 0.7 + 0.15,
      tw: Math.random() * Math.PI * 2, // twinkle phase
      ts: Math.random() * 0.012 + 0.006,
    }));

    // ── 2. Particles (gold + silver network) ──────────────────────
    const P_COUNT = window.innerWidth < 768 ? 55 : 100;
    const particles = Array.from({ length: P_COUNT }, () => ({
      x:    Math.random() * canvas.width,
      y:    Math.random() * canvas.height,
      vx:   (Math.random() - 0.5) * 0.45,
      vy:   (Math.random() - 0.5) * 0.45,
      r:    Math.random() * 1.8 + 0.5,
      gold: Math.random() > 0.62,
    }));

    // ── 3. Floating legal glyphs ───────────────────────────────────
    const GLYPHS = ["§", "⚖", "✦", "⊕", "Ω", "§", "⊗", "⬡"];
    const glyphs = Array.from({ length: 10 }, (_, i) => ({
      x:    Math.random() * canvas.width,
      y:    Math.random() * canvas.height,
      size: Math.random() * 28 + 18,
      vy:   -(Math.random() * 0.25 + 0.08),
      vx:   (Math.random() - 0.5) * 0.12,
      a:    Math.random() * 0.12 + 0.04,
      char: GLYPHS[i % GLYPHS.length],
      rot:  Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.003,
    }));

    // ── 4. Flowing aurora wave lines ───────────────────────────────
    const WAVES = [
      { amp: 90,  freq: 0.0018, speed: 0.008,  y: 0.28, col: "rgba(201,168,76,",  w: 1.2, a: 0.18 },
      { amp: 70,  freq: 0.0022, speed: 0.006,  y: 0.48, col: "rgba(30,80,160,",   w: 1.0, a: 0.22 },
      { amp: 110, freq: 0.0014, speed: 0.005,  y: 0.65, col: "rgba(201,168,76,",  w: 0.8, a: 0.10 },
      { amp: 60,  freq: 0.0026, speed: 0.009,  y: 0.82, col: "rgba(60,120,200,",  w: 0.9, a: 0.14 },
      { amp: 80,  freq: 0.0016, speed: 0.007,  y: 0.15, col: "rgba(201,140,60,",  w: 0.7, a: 0.09 },
    ];

    // ── 5. Perspective grid ────────────────────────────────────────
    const drawGrid = (w, h) => {
      const hoz = 0.52; // horizon at 52% from top
      const vanX = w * 0.5;
      const vanY = h * hoz;
      const COLS = 12, ROWS = 10;
      ctx.save();
      ctx.globalAlpha = 0.06;
      ctx.strokeStyle = "#C9A84C";
      ctx.lineWidth = 0.7;

      // vertical perspective lines
      for (let c = 0; c <= COLS; c++) {
        const bx = (c / COLS) * w;
        ctx.beginPath();
        ctx.moveTo(vanX, vanY);
        ctx.lineTo(bx, h);
        ctx.stroke();
      }
      // horizontal lines
      for (let r = 0; r <= ROWS; r++) {
        const fy  = r / ROWS;
        const py  = vanY + (h - vanY) * (fy * fy);
        const fade = fy;
        ctx.globalAlpha = fade * 0.07;
        ctx.beginPath();
        ctx.moveTo(0, py);
        ctx.lineTo(w, py);
        ctx.stroke();
      }
      ctx.restore();
    };

    // ── Draw loop ──────────────────────────────────────────────────
    const draw = () => {
      const W = canvas.width, H = canvas.height;
      t += 1;

      // — Background gradient —
      const bg = ctx.createLinearGradient(0, 0, W * 0.6, H);
      bg.addColorStop(0,   "#040710");
      bg.addColorStop(0.4, "#070C18");
      bg.addColorStop(0.8, "#050810");
      bg.addColorStop(1,   "#030508");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);

      // — Deep radial glow top-right (gold) —
      const gr1 = ctx.createRadialGradient(W * 0.82, H * 0.08, 0, W * 0.82, H * 0.08, W * 0.55);
      gr1.addColorStop(0,   "rgba(201,168,76,0.13)");
      gr1.addColorStop(0.5, "rgba(201,120,40,0.05)");
      gr1.addColorStop(1,   "transparent");
      ctx.fillStyle = gr1;
      ctx.fillRect(0, 0, W, H);

      // — Deep radial glow bottom-left (blue) —
      const gr2 = ctx.createRadialGradient(W * 0.1, H * 0.9, 0, W * 0.1, H * 0.9, W * 0.5);
      gr2.addColorStop(0,   "rgba(30,80,180,0.18)");
      gr2.addColorStop(0.5, "rgba(20,50,130,0.07)");
      gr2.addColorStop(1,   "transparent");
      ctx.fillStyle = gr2;
      ctx.fillRect(0, 0, W, H);

      // — Centre ambient glow (breathes) —
      const breathe = 0.5 + 0.5 * Math.sin(t * 0.018);
      const gr3 = ctx.createRadialGradient(W * 0.5, H * 0.42, 0, W * 0.5, H * 0.42, W * 0.38);
      gr3.addColorStop(0,   `rgba(201,168,76,${0.04 + breathe * 0.04})`);
      gr3.addColorStop(1,   "transparent");
      ctx.fillStyle = gr3;
      ctx.fillRect(0, 0, W, H);

      // — Stars —
      stars.forEach(s => {
        s.tw += s.ts;
        const tw = 0.5 + 0.5 * Math.sin(s.tw);
        ctx.beginPath();
        ctx.arc(s.x * W, s.y * H, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(240,232,210,${s.a * tw})`;
        ctx.fill();
      });

      // — Perspective grid —
      drawGrid(W, H);

      // — Aurora wave lines —
      WAVES.forEach(wave => {
        ctx.beginPath();
        for (let x = 0; x <= W; x += 3) {
          const y = H * wave.y +
            wave.amp * Math.sin(x * wave.freq + t * wave.speed) +
            (wave.amp * 0.4) * Math.sin(x * wave.freq * 2.3 + t * wave.speed * 1.6);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.strokeStyle = wave.col + wave.a + ")";
        ctx.lineWidth = wave.w;
        ctx.stroke();
      });

      // — Glowing horizontal accent lines —
      [0.33, 0.67].forEach((frac, i) => {
        const y = H * frac + 12 * Math.sin(t * 0.009 + i * 2.4);
        const grad = ctx.createLinearGradient(0, y, W, y);
        grad.addColorStop(0,   "transparent");
        grad.addColorStop(0.2, `rgba(201,168,76,${0.07 + i * 0.02})`);
        grad.addColorStop(0.5, `rgba(201,168,76,${0.14 + i * 0.03})`);
        grad.addColorStop(0.8, `rgba(201,168,76,${0.07 + i * 0.02})`);
        grad.addColorStop(1,   "transparent");
        ctx.strokeStyle = grad;
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        ctx.moveTo(0, y); ctx.lineTo(W, y);
        ctx.stroke();
      });

      // — Particle network —
      const LINK_DIST = 140;
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < LINK_DIST) {
            const alpha = (1 - d / LINK_DIST) * 0.22;
            const isGold = particles[i].gold || particles[j].gold;
            ctx.beginPath();
            ctx.strokeStyle = isGold
              ? `rgba(201,168,76,${alpha})`
              : `rgba(168,180,200,${alpha * 0.7})`;
            ctx.lineWidth = isGold ? 0.8 : 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        if (p.gold) {
          ctx.fillStyle = "rgba(201,168,76,0.85)";
          // soft glow
          ctx.shadowColor  = "rgba(201,168,76,0.6)";
          ctx.shadowBlur   = 6;
        } else {
          ctx.fillStyle   = "rgba(168,180,200,0.45)";
          ctx.shadowBlur  = 0;
        }
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // — Floating legal glyphs —
      glyphs.forEach(g => {
        g.y += g.vy; g.x += g.vx; g.rot += g.rotV;
        if (g.y < -60) { g.y = H + 40; g.x = Math.random() * W; }
        ctx.save();
        ctx.translate(g.x, g.y);
        ctx.rotate(g.rot);
        ctx.font = `${g.size}px 'Cormorant Garamond', serif`;
        ctx.fillStyle = `rgba(201,168,76,${g.a})`;
        ctx.textAlign = "center";
        ctx.fillText(g.char, 0, 0);
        ctx.restore();
      });

      // — Orbiting rings (two, counter-rotating) —
      const cx = W * 0.5, cy = H * 0.5;
      [
        { r: Math.min(W, H) * 0.32, speed: 0.004, dashes: [6, 18], alpha: 0.09 },
        { r: Math.min(W, H) * 0.46, speed: -0.003, dashes: [3, 24], alpha: 0.05 },
      ].forEach(ring => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(t * ring.speed);
        ctx.beginPath();
        ctx.arc(0, 0, ring.r, 0, Math.PI * 2);
        ctx.setLineDash(ring.dashes);
        ctx.strokeStyle = `rgba(201,168,76,${ring.alpha})`;
        ctx.lineWidth = 0.8;
        ctx.stroke();
        ctx.setLineDash([]);
        // dot on ring
        ctx.beginPath();
        ctx.arc(ring.r, 0, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,0.7)`;
        ctx.shadowColor = "rgba(201,168,76,0.8)";
        ctx.shadowBlur  = 10;
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.restore();
      });

      // — Top & bottom gold gradient vignette —
      const topFade = ctx.createLinearGradient(0, 0, 0, H * 0.25);
      topFade.addColorStop(0, "rgba(3,5,8,0.7)");
      topFade.addColorStop(1, "transparent");
      ctx.fillStyle = topFade;
      ctx.fillRect(0, 0, W, H * 0.25);

      const botFade = ctx.createLinearGradient(0, H * 0.78, 0, H);
      botFade.addColorStop(0, "transparent");
      botFade.addColorStop(1, "rgba(3,5,8,0.65)");
      ctx.fillStyle = botFade;
      ctx.fillRect(0, H * 0.78, W, H * 0.22);

      animId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // ── Search logic (unchanged) ────────────────────────────────────
  const pageList = [
    { name: "Home", url: "/" },
    { name: "About Us", url: "/about" },
    { name: "Contact", url: "/contact" },
    { name: "Services", url: "/services" },
    { name: "Testimonials", url: "/testimonials" },
    { name: "Business Setup", url: "/services/arbitration" },
    { name: "Civil & Criminal", url: "/services/civil-criminal" },
    { name: "Criminal Cases", url: "/services/criminal-cases" },
    { name: "Property Registry", url: "/services/property-registry" },
    { name: "GST Returns", url: "/services/gst-returns" },
    { name: "Cyber Fraud", url: "/services/cyber-fraud" },
    { name: "Trademark", url: "/services/trademark" },
    { name: "Notarization", url: "/services/notarization" },
    { name: "Court Marriage", url: "/services/court-marriage" },
    { name: "Company Registration", url: "/services/private-limited" },
    { name: "Legal Notices", url: "/services/legal-notices" },
    { name: "Contract Drafting", url: "/services/contract-drafting" },
  ];

  const handleSearchInput = (e) => {
    const query = e.target.value.trim().toLowerCase();
    setSearchQuery(query);
    if (query.length === 0) { setShowResults(false); return; }
    const matches = pageList.filter(p => p.name.toLowerCase().includes(query));
    setSearchResults(matches);
    setShowResults(matches.length > 0);
  };

  const handleSearchResultClick = (url) => {
    window.location.href = url;
    setShowResults(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".hero-search-container")) setShowResults(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <HeroSection>
      {/* Video background — dimmed heavily, canvas sits on top */}
      <HeroBgMedia>
        <video
          autoPlay muted loop playsInline
          poster="/assets/logo.png"
          onError={e => { e.target.style.display = "none"; }}
        >
          <source src="/assets/video/3129902-uhd_3840_2160_25fps.mp4" type="video/mp4" />
        </video>
      </HeroBgMedia>

      {/* Animated particle network */}
      <HeroCanvas ref={canvasRef} />

      {/* Ambient glow orbs */}
      <HeroOrbs />

      {/* Orbiting gold ring */}
      <HeroRing />

      {/* Horizontal scan line */}
      <ScanLine />

      {/* Dark radial overlay — keeps text legible */}
      <HeroOverlay />

      {/* Subtle floating legal icons */}
      <HeroFloatingIcons>
        <i className="fas fa-balance-scale"></i>
        <i className="fas fa-gavel"></i>
        <i className="fas fa-briefcase"></i>
        <i className="fas fa-user-shield"></i>
        <i className="fas fa-file-contract"></i>
      </HeroFloatingIcons>

      {/* Main content */}
      <HeroMain>

        <EyebrowTag>Premier Legal & Compliance Consultancy</EyebrowTag>

        <HeroTitle>
          <span className="main-title">Your Professional Journey</span>
          <span className="sub-title">
            <span>Simplified.</span>
            <span>Secured.</span>
            <span>Solved.</span>
          </span>
        </HeroTitle>

        <HeroSubtitle>
          Experience <span className="highlight">clarity</span>,{" "}
          <span className="highlight">confidence</span>, and{" "}
          <span className="highlight">care</span> — every step with Mundra
          Exim Consultants.
        </HeroSubtitle>

        <HeroSearchContainer className="hero-search-container">
          <HeroSearchWrapper>
            <span className="search-icon">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              placeholder="Try 'Trademark Registration', 'GST', 'Company'..."
              value={searchQuery}
              onChange={handleSearchInput}
            />
          </HeroSearchWrapper>
          <SearchResults className={showResults ? "show" : ""}>
            {searchResults.map((result, index) => (
              <li key={index} onClick={() => handleSearchResultClick(result.url)}>
                {result.name}
              </li>
            ))}
          </SearchResults>
        </HeroSearchContainer>

        <HeroButtons>
          <HeroButton href="/services/trademark">
            <i className="fas fa-trademark"></i> Trademark Registration
          </HeroButton>
          <HeroButton href="/services/gst-returns">
            <i className="fas fa-file-invoice-dollar"></i> GST Registration
          </HeroButton>
          <HeroButton href="/services/arbitration">
            <i className="fas fa-building"></i> Company Registration
          </HeroButton>
          <HeroButton href="/contact">
            <i className="fas fa-user-tie"></i> Lawyer Consultation
          </HeroButton>
        </HeroButtons>

      </HeroMain>
    </HeroSection>
  );
};

export default Hero;